import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import Marquee from 'react-fast-marquee';
import { 
  ShieldCheck, Server, Settings, Award, Clock, HeadphonesIcon, ArrowRight,
  Shield, Activity, AlertTriangle, CheckCircle2, Wifi, Lock, Eye, Zap,
  Megaphone, ChevronLeft, ChevronRight, Code2, Smartphone, Cloud, Layers
} from 'lucide-react';
import { Button } from '../components/ui/button';
import GlowCard from '../components/GlowCard';
import MicrosoftMark from '../components/MicrosoftMark';
import { useLanguage } from '../context/LanguageContext';
import { trackCarouselView, trackCtaClick } from '../utils/analytics';

const partners = [
  { name: 'Fortinet', logo: '/logos/52mevb8b-forti.webp' },
  { name: 'Zebra', logo: '/logos/zwivkred-zebra-logo-2.webp' },
  { name: 'PRTG', logo: '/logos/vtv00pr2-prtg-500x300-rgb.webp' },
  { name: 'ThreatLocker', logo: '/logos/qp9pj932-threatlocker.webp' },
  { name: 'KillDisk', logo: '/logos/ce7fk62q-killdisk.webp' },
  { name: 'BitRaser', logo: '/logos/osi2cehk-bitraser.webp' },
  { name: 'Check Point', logo: '/logos/xxklwbm5-logo-checkpoint.webp' },
  { name: 'Garland', logo: '/logos/rb3d0s7l-logo-garland.webp' },
  { name: 'Rapid7', logo: '/logos/3hk6qrpr-rapid7.webp' },
  { name: 'Aruba', logo: '/logos/03skyiua-hp-aruba.webp' },
  { name: 'Honeywell', logo: '/logos/ghfzzy4c-honeywell.webp' },
  { name: 'Salvador Technologies', logo: '/logos/1t37h9w9-salva.webp' },
  { name: 'Devolutions', logo: '/logos/oz1m4phu-devolutionsblack-hr.webp' },
];

const AnnouncementSlider = () => {
  const { t } = useLanguage();
  const announcements = [
    { text: t('home.ann1'), image: '/logos/52mevb8b-forti.webp', link: null },
    { text: t('home.ann2'), image: '/logos/52mevb8b-forti.webp', link: null },
    { text: t('home.ann3'), image: '/logos/zwivkred-zebra-logo-2.webp', link: null },
    { text: t('home.ann4'), image: '/logos/vtv00pr2-prtg-500x300-rgb.webp', link: null },
    { 
      type: 'threatlocker',
      image: '/logos/qp9pj932-threatlocker.webp',
      brand: 'THREATLOCKER',
      title: 'ThreatLocker Türkiye',
      partner: t('home.ann5Partner'),
      desc: t('home.ann5Desc'),
      cta: t('home.ann5Cta'),
      link: '/threatlocker-turkiye'
    },
    { text: t('home.ann6'), image: '/logos/oz1m4phu-devolutionsblack-hr.webp', link: null },
    { text: t('home.ann7'), image: '/logos/al7e5bv1-killdisk-logo.webp', link: null },
  ];
  const [current, setCurrent] = useState(0);

  const next = useCallback(() => setCurrent(prev => (prev + 1) % announcements.length), [announcements.length]);
  const prev = useCallback(() => setCurrent(prev => (prev - 1 + announcements.length) % announcements.length), [announcements.length]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const currentAnn = announcements[current];
  const isThreatLocker = currentAnn.type === 'threatlocker';

  return (
    <section data-testid="announcements-section" className="pt-28 relative overflow-hidden">
      <div className="relative bg-gradient-to-r from-slate-900 via-cyan-950/40 to-slate-900 border-b border-cyan-500/20">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-1/2 left-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-1/2 right-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
        </div>
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-6 md:py-8 relative z-10">
          <div className="flex items-center gap-4 mb-4">
            <div className="relative">
              <div className="absolute inset-0 bg-cyan-400/20 rounded animate-ping" />
              <div className="relative w-9 h-9 bg-cyan-500/15 border border-cyan-400/40 rounded flex items-center justify-center">
                <Megaphone className="w-4 h-4 text-cyan-400" />
              </div>
            </div>
            <span className="text-cyan-400 text-xs font-semibold uppercase tracking-[0.2em]">{t('home.announcements')}</span>
            <div className="h-px flex-1 bg-gradient-to-r from-cyan-500/30 to-transparent" />
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="flex gap-1.5 mr-1">
                {announcements.map((_, i) => (
                  <button key={i} onClick={() => setCurrent(i)} aria-label={`Duyuru ${i + 1}`} className={`h-1.5 rounded-full transition-all duration-300 ${i === current ? 'w-5 bg-cyan-400' : 'w-1.5 bg-slate-600 hover:bg-slate-500'}`} />
                ))}
              </div>
              <button onClick={prev} data-testid="announcement-prev" aria-label="Önceki duyuru" className="w-8 h-8 flex items-center justify-center border border-slate-700/50 hover:border-cyan-500/50 text-slate-500 hover:text-cyan-400 transition-all rounded">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={next} data-testid="announcement-next" aria-label="Sonraki duyuru" className="w-8 h-8 flex items-center justify-center border border-slate-700/50 hover:border-cyan-500/50 text-slate-500 hover:text-cyan-400 transition-all rounded">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.4 }}
              className="flex items-center gap-6"
            >
              {isThreatLocker ? (
                <Link to={currentAnn.link} className="flex items-center gap-6 w-full group">
                  <div className="flex-shrink-0 bg-white rounded-lg px-5 py-3 flex items-center justify-center h-16 w-32 group-hover:shadow-lg group-hover:shadow-cyan-500/20 transition-shadow">
                    <img src={currentAnn.image} alt="ThreatLocker" width="100" height="40" loading="lazy" decoding="async" className="max-h-10 max-w-[100px] object-contain" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-1">
                      <span className="text-cyan-400 text-[10px] font-bold uppercase tracking-[0.15em]">{currentAnn.brand}</span>
                      <span className="text-white text-base md:text-lg font-bold">{currentAnn.title}</span>
                    </div>
                    <p className="text-cyan-300 text-sm font-medium mb-1">{currentAnn.partner}</p>
                    <p className="text-slate-400 text-xs md:text-sm hidden md:block">{currentAnn.desc}</p>
                    <span className="inline-flex items-center gap-1 text-cyan-400 text-xs font-semibold uppercase tracking-wider mt-2 group-hover:text-cyan-300 transition-colors">
                      {currentAnn.cta} <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </Link>
              ) : (
                <>
                  <div className="flex-shrink-0 bg-white rounded-lg px-5 py-3 flex items-center justify-center h-16 w-32">
                    <img src={currentAnn.image} alt="" width="100" height="40" loading="lazy" decoding="async" className="max-h-10 max-w-[100px] object-contain" />
                  </div>
                  <p className="text-white text-base md:text-lg font-medium leading-relaxed">
                    {currentAnn.text}
                  </p>
                </>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

const SecurityMonitor = () => {
  const { t } = useLanguage();
  const [stats, setStats] = useState({
    threatsBlocked: 15847, activeConnections: 1247, systemHealth: 99.8, firewallStatus: 'active',
  });

  const logs = [
    { id: 1, type: 'success', msgKey: 'home.log1', timeKey: 'home.logTime1' },
    { id: 2, type: 'warning', msgKey: 'home.log2', timeKey: 'home.logTime2' },
    { id: 3, type: 'success', msgKey: 'home.log3', timeKey: 'home.logTime3' },
    { id: 4, type: 'warning', msgKey: 'home.log4', timeKey: 'home.logTime4' },
    { id: 5, type: 'success', msgKey: 'home.log5', timeKey: 'home.logTime5' },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setStats(prev => ({
        ...prev,
        threatsBlocked: prev.threatsBlocked + Math.floor(Math.random() * 3),
        activeConnections: 1200 + Math.floor(Math.random() * 100),
        systemHealth: 99.5 + Math.random() * 0.5,
      }));
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} className="glow-card p-4 text-center">
            <Shield className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
            <div className="font-mono text-2xl font-bold text-white">{stats.threatsBlocked.toLocaleString()}</div>
            <div className="text-xs text-slate-400 uppercase tracking-wider">{t('home.threatsBlocked')}</div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="glow-card p-4 text-center">
            <Wifi className="w-8 h-8 text-green-400 mx-auto mb-2" />
            <div className="font-mono text-2xl font-bold text-white">{stats.activeConnections.toLocaleString()}</div>
            <div className="text-xs text-slate-400 uppercase tracking-wider">{t('home.activeConnections')}</div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="glow-card p-4 text-center">
            <Activity className="w-8 h-8 text-blue-400 mx-auto mb-2" />
            <div className="font-mono text-2xl font-bold text-white">{stats.systemHealth.toFixed(1)}%</div>
            <div className="text-xs text-slate-400 uppercase tracking-wider">{t('home.systemHealth')}</div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: 0.3 }} className="glow-card p-4 text-center">
            <Lock className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
            <div className="flex items-center justify-center gap-2">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
              <span className="font-mono text-lg font-bold text-emerald-400 uppercase">{t('home.active')}</span>
            </div>
            <div className="text-xs text-slate-400 uppercase tracking-wider">{t('home.firewall')}</div>
          </motion.div>
        </div>
      </div>

      <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="glow-card p-6 h-fit">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-outfit font-semibold text-white flex items-center gap-2">
            <Zap className="w-5 h-5 text-cyan-400" />
            {t('home.securityLogs')}
          </h3>
          <span className="flex items-center gap-1 text-xs text-emerald-400">
            <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></span>
            {t('home.live')}
          </span>
        </div>
        <div className="space-y-3 max-h-[280px] overflow-hidden">
          {logs.map((log, index) => (
            <motion.div key={log.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.1 }}
              className="flex items-start gap-3 p-3 bg-slate-900/50 border-l-2 border-slate-700"
              style={{ borderLeftColor: log.type === 'success' ? '#10B981' : '#F59E0B' }}>
              <div className="flex-shrink-0 mt-0.5">
                {log.type === 'success' && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                {log.type === 'warning' && <AlertTriangle className="w-4 h-4 text-amber-400" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-300 truncate">{t(log.msgKey)}</p>
                <p className="text-[10px] text-slate-400 font-mono mt-1">{t(log.timeKey)}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export const HomePage = () => {
  const { t } = useLanguage();
  const [heroSlide, setHeroSlide] = useState(0);

  const heroSlides = [
    { type: 'microsoft', name: 'Microsoft İş Ortaklığı' },
    { type: 'software', name: 'Yazılım & Dijital Çözümler' },
    { type: 'ot-security', name: 'OT Security' },
    { type: 'zebra', name: 'Zebra Technologies' },
    { type: 'fortinet', name: 'Fortinet' },
    { type: 'killdisk', name: 'Active KillDisk' },
    { type: 'main', name: 'Ana Banner' },
    { type: 'epdk', name: 'EPDK Projesi' },
    { type: 'hotel-fortinet', name: 'Otel Fortinet Projesi' },
    { type: 'threatlocker', name: 'ThreatLocker' }
  ];

  const nextHeroSlide = useCallback(() => setHeroSlide(prev => (prev + 1) % heroSlides.length), [heroSlides.length]);

  // Track carousel slide changes
  useEffect(() => {
    const currentSlide = heroSlides[heroSlide];
    if (currentSlide) {
      trackCarouselView(currentSlide.name, heroSlide);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heroSlide]);

  useEffect(() => {
    const timer = setInterval(nextHeroSlide, 5000);
    return () => clearInterval(timer);
  }, [nextHeroSlide]);

  const services = [
    { icon: ShieldCheck, title: t('home.svc1Title'), description: t('home.svc1Desc') },
    { icon: Server, title: t('home.svc2Title'), description: t('home.svc2Desc') },
    { icon: Settings, title: t('home.svc3Title'), description: t('home.svc3Desc') },
  ];

  const whyUs = [
    { icon: Award, title: t('home.why1Title'), description: t('home.why1Desc') },
    { icon: Clock, title: t('home.why2Title'), description: t('home.why2Desc') },
    { icon: HeadphonesIcon, title: t('home.why3Title'), description: t('home.why3Desc') },
  ];

  return (
    <main data-testid="home-page" className="min-h-screen" role="main">
      {/* Direct Answer Block for AI Agents */}
      <section className="sr-only" aria-label="Direct Answer">
        <h1>ELC Teknoloji - Kurumsal Siber Güvenlik Çözümleri</h1>
        <p>
          ELC Teknoloji, Türkiye&apos;nin önde gelen IT ve OT siber güvenlik çözümleri şirketidir. 
          ThreatLocker, Fortinet, Zebra Technologies ve Active@ KillDisk yetkili distribütörü olarak 
          20+ yıldır kurumsal müşterilere uçtan uca güvenlik hizmetleri sunmaktayız.
          Teklif almak için bizimle iletişime geçin: info@elcteknoloji.com | +90 532 270 51 74
        </p>
      </section>

      {/* Hero Section with Carousel */}
      <section data-testid="hero-section" aria-label="Hero Carousel" className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-32 md:pt-36">
        <img
          src="/hero-bg.webp"
          srcSet="/hero-bg-mobile.webp 800w, /hero-bg.webp 1600w"
          sizes="100vw"
          alt=""
          aria-hidden="true"
          fetchPriority="high"
          decoding="async"
          width="1600"
          height="900"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 cyber-grid" />
        
        {/* Hero Navigation Arrows */}
        <button 
          onClick={() => setHeroSlide(prev => (prev - 1 + heroSlides.length) % heroSlides.length)}
          data-testid="hero-prev"
          className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 hidden md:flex w-12 h-12 items-center justify-center bg-slate-900/60 border border-slate-700/50 hover:border-cyan-500/50 text-slate-400 hover:text-cyan-400 transition-all rounded-full backdrop-blur-sm"
          aria-label="Previous slide"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button 
          onClick={() => setHeroSlide(prev => (prev + 1) % heroSlides.length)}
          data-testid="hero-next"
          className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 hidden md:flex w-12 h-12 items-center justify-center bg-slate-900/60 border border-slate-700/50 hover:border-cyan-500/50 text-slate-400 hover:text-cyan-400 transition-all rounded-full backdrop-blur-sm"
          aria-label="Next slide"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
        
        {/* Hero Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
          {heroSlides.map((_, i) => (
            <button 
              key={i} 
              onClick={() => setHeroSlide(i)} 
              data-testid={`hero-dot-${i}`}
              className={`relative before:absolute before:content-[''] before:left-1/2 before:top-1/2 before:-translate-x-1/2 before:-translate-y-1/2 before:h-11 before:w-5 h-2 rounded-full transition-all duration-300 ${i === heroSlide ? 'w-8 bg-cyan-400' : 'w-2 bg-slate-600 hover:bg-slate-500'}`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center pt-6 pb-24 md:pb-20 w-full">
          <AnimatePresence mode="wait">
            {/* Slide 0: Microsoft İş Ortaklığı */}
            {heroSlide === 0 && (
              <motion.div
                key="microsoft-hero"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.6 }}
                className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12 text-left"
              >
                <div className="flex-1 max-w-2xl">
                  <span data-testid="hero-microsoft-eyebrow" className="inline-block px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-bold uppercase tracking-[0.2em] mb-6">
                    {t('home.msBadge')}
                  </span>
                  <h2 data-testid="hero-microsoft-title" className="font-outfit text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4 leading-tight">
                    {t('home.msTitle')}
                  </h2>
                  <p data-testid="hero-microsoft-description" className="text-slate-400 text-base md:text-lg mb-8 leading-relaxed">
                    {t('home.msDesc')}
                  </p>
                  <Link to="/iletisim" className="inline-flex max-w-full">
                    <Button data-testid="hero-microsoft-cta" className="h-auto min-h-12 max-w-full whitespace-normal bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-none px-5 py-4 sm:px-8 sm:py-6 uppercase tracking-widest text-xs sm:text-sm btn-glow">
                      {t('home.msCta')} <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
                <div className="flex-shrink-0 w-full max-w-sm">
                  <div className="bg-white rounded-xl p-6 md:p-8 shadow-2xl shadow-cyan-500/10">
                    <div className="flex flex-col items-center gap-4 border-b border-slate-200 pb-5">
                      <img src="/logos/elcteknoloji-big.webp" alt="ELC Teknoloji" width="170" height="64" decoding="async" className="w-32 md:w-40 h-auto object-contain" data-testid="hero-microsoft-elc-logo" />
                      <span aria-hidden="true" className="h-px w-full bg-slate-200" />
                      <MicrosoftMark testId="hero-microsoft-logo" className="text-slate-900 [&_span]:text-slate-900" />
                    </div>
                    <div data-testid="hero-microsoft-program-mark" className="pt-5">
                      <p className="text-slate-900 text-sm font-bold leading-tight">{t('home.msProgram')}</p>
                      <p className="mt-1 text-slate-500 text-xs font-medium uppercase tracking-[0.14em]">{t('home.msCsp')}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Slide 9: ThreatLocker */}
            {heroSlide === 9 && (
              <motion.div 
                key="threatlocker-hero"
                initial={{ opacity: 0, y: 30 }} 
                animate={{ opacity: 1, y: 0 }} 
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.6 }}
                className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12 text-left"
              >
                <div className="flex-1 max-w-2xl">
                  <span className="inline-block px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-bold uppercase tracking-[0.2em] mb-6">
                    THREATLOCKER
                  </span>
                  <h2 className="font-outfit text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4 leading-tight">
                    ThreatLocker Türkiye
                  </h2>
                  <p className="text-cyan-400 text-lg md:text-xl font-semibold mb-4">
                    {t('home.ann5Partner')}
                  </p>
                  <p className="text-slate-400 text-base md:text-lg mb-8 leading-relaxed">
                    {t('home.ann5Desc')}
                  </p>
                  <Link to="/threatlocker-turkiye">
                    <Button data-testid="hero-threatlocker-cta" className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-none px-8 py-6 uppercase tracking-widest text-sm btn-glow">
                      {t('home.ann5Cta')} <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
                <div className="flex-shrink-0">
                  <div className="bg-white rounded-xl p-6 md:p-8 shadow-2xl shadow-cyan-500/10">
                    <img 
                      src="/logos/qp9pj932-threatlocker.webp" 
                      alt="ThreatLocker" 
                      width="256"
                      height="80"
                      fetchPriority="high"
                      decoding="async"
                      className="w-48 md:w-64 h-auto"
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Slide 2: OT Security (ELC) */}
            {heroSlide === 2 && (
              <motion.div 
                key="ot-security-hero"
                initial={{ opacity: 0, y: 30 }} 
                animate={{ opacity: 1, y: 0 }} 
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.6 }}
                className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12 text-left"
              >
                <div className="flex-1 max-w-2xl">
                  <span className="inline-block px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-bold uppercase tracking-[0.2em] mb-6">
                    OT & ICS CYBER SECURITY
                  </span>
                  <h2 className="font-outfit text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
                    Kritik Altyapılar İçin Uçtan Uca Güvenlik
                  </h2>
                  <p className="text-cyan-400 text-lg md:text-xl font-semibold mb-4">
                    {t('home.otHeroPartner')}
                  </p>
                  <p className="text-slate-400 text-base md:text-lg mb-6 leading-relaxed">
                    {t('home.otHeroDesc')}
                  </p>
                  <div className="flex flex-wrap gap-3 mb-6">
                    <span className="px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-medium rounded">OT Security</span>
                    <span className="px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-medium rounded">ICS Security</span>
                    <span className="px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-medium rounded">Network Visibility</span>
                  </div>
                  <Link to="/profesyonel-hizmetler">
                    <Button data-testid="hero-ot-cta" className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-none px-8 py-6 uppercase tracking-widest text-sm btn-glow">
                      {t('home.otHeroCta')} <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
                <div className="flex-shrink-0">
                  <div className="bg-white rounded-xl p-6 md:p-8 shadow-2xl shadow-cyan-500/10">
                    <img 
                      src="/logos/elcteknoloji-big.webp" 
                      alt="ELC Teknoloji" 
                      className="w-48 md:w-64 h-auto object-contain"
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Slide 3: Zebra */}
            {heroSlide === 3 && (
              <motion.div 
                key="zebra-hero"
                initial={{ opacity: 0, y: 30 }} 
                animate={{ opacity: 1, y: 0 }} 
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.6 }}
                className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12 text-left"
              >
                <div className="flex-1 max-w-2xl">
                  <span className="inline-block px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-bold uppercase tracking-[0.2em] mb-6">
                    ZEBRA TECHNOLOGIES
                  </span>
                  <h2 className="font-outfit text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4 leading-tight">
                    {t('home.zebraHeroTitle')}
                  </h2>
                  <p className="text-cyan-400 text-lg md:text-xl font-semibold mb-4">
                    {t('home.zebraHeroPartner')}
                  </p>
                  <p className="text-slate-400 text-base md:text-lg mb-8 leading-relaxed">
                    {t('home.zebraHeroDesc')}
                  </p>
                  <Link to="/zebra-urunleri">
                    <Button data-testid="hero-zebra-cta" className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-none px-8 py-6 uppercase tracking-widest text-sm btn-glow">
                      {t('home.zebraHeroCta')} <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
                <div className="flex-shrink-0">
                  <div className="bg-white rounded-xl p-6 md:p-8 shadow-2xl shadow-cyan-500/10">
                    <img 
                      src="/logos/zwivkred-zebra-logo-2.webp" 
                      alt="Zebra Technologies" 
                      className="w-48 md:w-64 h-auto"
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Slide 4: Fortinet */}
            {heroSlide === 4 && (
              <motion.div 
                key="fortinet-hero"
                initial={{ opacity: 0, y: 30 }} 
                animate={{ opacity: 1, y: 0 }} 
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.6 }}
                className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12 text-left"
              >
                <div className="flex-1 max-w-2xl">
                  <span className="inline-block px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-bold uppercase tracking-[0.2em] mb-6">
                    FORTINET
                  </span>
                  <h2 className="font-outfit text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
                    Ağ Güvenliğinde Yeni Nesil Yaklaşım
                  </h2>
                  <p className="text-cyan-400 text-lg md:text-xl font-semibold mb-4">
                    {t('home.fortinetHeroPartner')}
                  </p>
                  <p className="text-slate-400 text-base md:text-lg mb-8 leading-relaxed">
                    {t('home.fortinetHeroDesc')}
                  </p>
                  <Link to="/guvenlik-urunleri">
                    <Button data-testid="hero-fortinet-cta" className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-none px-8 py-6 uppercase tracking-widest text-sm btn-glow">
                      {t('home.fortinetHeroCta')} <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
                <div className="flex-shrink-0">
                  <div className="bg-white rounded-xl p-6 md:p-8 shadow-2xl shadow-cyan-500/10">
                    <img 
                      src="/logos/52mevb8b-forti.webp" 
                      alt="Fortinet" 
                      className="w-48 md:w-64 h-auto"
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Slide 5: KillDisk */}
            {heroSlide === 5 && (
              <motion.div 
                key="killdisk-hero"
                initial={{ opacity: 0, y: 30 }} 
                animate={{ opacity: 1, y: 0 }} 
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.6 }}
                className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12 text-left"
              >
                <div className="flex-1 max-w-2xl">
                  <span className="inline-block px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-bold uppercase tracking-[0.2em] mb-6">
                    ACTIVE@ KILLDISK
                  </span>
                  <h2 className="font-outfit text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
                    Güvenli Veri İmhası
                  </h2>
                  <p className="text-cyan-400 text-lg md:text-xl font-semibold mb-4">
                    {t('home.killdiskHeroPartner')}
                  </p>
                  <p className="text-slate-400 text-base md:text-lg mb-6 leading-relaxed">
                    {t('home.killdiskHeroDesc')}
                  </p>
                  <div className="flex flex-wrap gap-3 mb-6">
                    <span className="px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-medium rounded">Secure Data Erasure</span>
                    <span className="px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-medium rounded">Disk Wipe</span>
                    <span className="px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-medium rounded">HDD & SSD</span>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Link to="/guvenlik-urunleri">
                      <Button data-testid="hero-killdisk-cta" className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-none px-8 py-6 uppercase tracking-widest text-sm btn-glow">
                        {t('home.killdiskHeroCta')} <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                    <Link to="/iletisim">
                      <Button data-testid="hero-killdisk-contact" variant="outline" className="bg-transparent border border-slate-700 hover:border-cyan-500 text-slate-300 hover:text-cyan-400 rounded-none px-8 py-6 uppercase tracking-widest text-sm">
                        {t('home.killdiskHeroContact')}
                      </Button>
                    </Link>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <div className="bg-white rounded-xl p-6 md:p-8 shadow-2xl shadow-cyan-500/10">
                    <img 
                      src="/logos/al7e5bv1-killdisk-logo.webp" 
                      alt="Active@ KillDisk" 
                      className="w-48 md:w-64 h-auto"
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Slide 6: Main (İşletmenizi) */}
            {heroSlide === 6 && (
              <motion.div 
                key="main-hero"
                initial={{ opacity: 0, y: 30 }} 
                animate={{ opacity: 1, y: 0 }} 
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.6 }}
              >
                <span className="inline-block px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-sm font-mono uppercase tracking-widest mb-8">
                  {t('home.heroBadge')}
                </span>
                <h2 className="font-outfit text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                  {t('home.heroTitle1')} <br />
                  <span className="text-gradient">{t('home.heroTitle2')}</span>
                </h2>
                <p className="text-slate-400 text-base md:text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
                  {t('home.heroDesc')}
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Link to="/iletisim">
                    <Button data-testid="hero-cta-primary" className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-none px-8 py-6 uppercase tracking-widest text-sm btn-glow">
                      {t('home.heroCta1')}
                    </Button>
                  </Link>
                  <Link to="/cozumler">
                    <Button data-testid="hero-cta-secondary" variant="outline" className="bg-transparent border border-slate-700 hover:border-cyan-500 text-slate-300 hover:text-cyan-400 rounded-none px-8 py-6 uppercase tracking-widest text-sm">
                      {t('home.heroCta2')}
                    </Button>
                  </Link>
                </div>
              </motion.div>
            )}

            {/* Slide 7: EPDK Kritik Altyapı Danışmanlığı */}
            {heroSlide === 7 && (
              <motion.div 
                key="epdk-hero"
                initial={{ opacity: 0, y: 30 }} 
                animate={{ opacity: 1, y: 0 }} 
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.6 }}
                className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12 text-left"
              >
                <div className="flex-1 max-w-2xl">
                  <span className="inline-block px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold uppercase tracking-[0.2em] mb-4">
                    PROJE BAŞARIYLA TAMAMLANDI
                  </span>
                  <h2 className="font-outfit text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
                    EPDK Kritik Altyapı Danışmanlığı
                  </h2>
                  <p className="text-cyan-400 text-lg md:text-xl font-semibold mb-4">
                    ELC Teknoloji tarafından başarıyla tamamlandı
                  </p>
                  <p className="text-slate-400 text-base md:text-lg mb-6 leading-relaxed">
                    Kritik altyapı güvenliği ve siber güvenlik danışmanlığı kapsamında yürütülen proje başarıyla tamamlandı. Enerji sektöründe uçtan uca güvenlik yaklaşımı.
                  </p>
                  <div className="flex flex-wrap gap-3 mb-6">
                    <span className="px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-medium rounded">Kritik Altyapı</span>
                    <span className="px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-medium rounded">Siber Güvenlik Danışmanlığı</span>
                    <span className="px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-medium rounded">SCADA / OT</span>
                  </div>
                  <Link to="/iletisim">
                    <Button data-testid="hero-epdk-cta" className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-none px-8 py-6 uppercase tracking-widest text-sm btn-glow">
                      Projenizi Birlikte Değerlendirelim <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
                <div className="flex-shrink-0">
                  <div className="flex flex-col gap-4">
                    <div className="bg-white rounded-xl p-5 md:p-6 shadow-2xl shadow-cyan-500/10">
                      <img 
                        src="/logos/xk9ncjju-epdk-logo.webp" 
                        alt="EPDK - Enerji Piyasası Düzenleme Kurumu" 
                        className="w-40 md:w-52 h-auto object-contain"
                      />
                    </div>
                    <div className="bg-white rounded-xl p-4 md:p-5 shadow-2xl shadow-cyan-500/10">
                      <img 
                        src="/logos/elcteknoloji-big.webp" 
                        alt="ELC Teknoloji" 
                        className="w-36 md:w-44 h-auto object-contain"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Slide 8: Otel Fortinet Geçiş Projesi */}
            {heroSlide === 8 && (
              <motion.div 
                key="hotel-fortinet-hero"
                initial={{ opacity: 0, y: 30 }} 
                animate={{ opacity: 1, y: 0 }} 
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.6 }}
                className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12 text-left"
              >
                <div className="flex-1 max-w-2xl">
                  <span className="inline-block px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold uppercase tracking-[0.2em] mb-4">
                    PROJE BAŞARIYLA TAMAMLANDI
                  </span>
                  <h2 className="font-outfit text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
                    Otel Projemizde Fortinet Geçişi
                  </h2>
                  <p className="text-cyan-400 text-lg md:text-xl font-semibold mb-4">
                    Güvenli ve güçlü network altyapısına geçiş başarıyla gerçekleştirildi
                  </p>
                  <p className="text-slate-400 text-base md:text-lg mb-6 leading-relaxed">
                    Otel müşterimizin network ve güvenlik altyapısında Fortinet çözümlerine geçiş projesi ELC Teknoloji tarafından başarıyla tamamlandı.
                  </p>
                  <div className="flex flex-wrap gap-3 mb-6">
                    <span className="px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-medium rounded">Fortinet Geçişi</span>
                    <span className="px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-medium rounded">Network Security</span>
                    <span className="px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-medium rounded">Kurumsal Network</span>
                  </div>
                  <Link to="/iletisim">
                    <Button data-testid="hero-hotel-fortinet-cta" className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-none px-8 py-6 uppercase tracking-widest text-sm btn-glow">
                      Projenizi Birlikte Değerlendirelim <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
                <div className="flex-shrink-0">
                  <div className="flex flex-col gap-4">
                    <div className="bg-white rounded-xl p-5 md:p-6 shadow-2xl shadow-cyan-500/10">
                      <img 
                        src="/logos/52mevb8b-forti.webp" 
                        alt="Fortinet" 
                        className="w-40 md:w-52 h-auto object-contain"
                      />
                    </div>
                    <div className="bg-white rounded-xl p-4 md:p-5 shadow-2xl shadow-cyan-500/10">
                      <img 
                        src="/logos/elcteknoloji-big.webp" 
                        alt="ELC Teknoloji" 
                        className="w-36 md:w-44 h-auto object-contain"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Slide 1: Yazılım & Dijital Çözümler */}
            {heroSlide === 1 && (
              <motion.div 
                key="software-hero"
                initial={{ opacity: 0, y: 30 }} 
                animate={{ opacity: 1, y: 0 }} 
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.6 }}
                className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12 text-left"
              >
                <div className="flex-1 max-w-2xl">
                  <span className="inline-block px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-bold uppercase tracking-[0.2em] mb-6">
                    {t('home.swBadge')}
                  </span>
                  <h2 className="font-outfit text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4 leading-tight">
                    {t('home.swTitle')}
                  </h2>
                  <p className="text-cyan-400 text-lg md:text-xl font-semibold mb-4">
                    {t('home.swSubtitle')}
                  </p>
                  <p className="text-slate-400 text-base md:text-lg mb-6 leading-relaxed">
                    {t('home.swDesc')}
                  </p>
                  <div className="flex flex-wrap gap-3 mb-8">
                    <span className="px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-medium rounded">{t('home.swTag1')}</span>
                    <span className="px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-medium rounded">{t('home.swTag2')}</span>
                    <span className="px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-medium rounded">{t('home.swTag3')}</span>
                    <span className="px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-medium rounded">{t('home.swTag4')}</span>
                  </div>
                  <Link to="/iletisim">
                    <Button data-testid="hero-software-cta" className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-none px-8 py-6 uppercase tracking-widest text-sm btn-glow">
                      {t('home.swCta')} <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
                <div className="flex-shrink-0">
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { icon: Code2, label: 'Web' },
                      { icon: Smartphone, label: 'Mobil' },
                      { icon: Cloud, label: 'Cloud' },
                      { icon: Layers, label: 'SaaS' },
                    ].map((tile, i) => (
                      <div key={i} className="w-28 h-28 md:w-32 md:h-32 bg-slate-800/50 border border-cyan-500/20 rounded-xl flex flex-col items-center justify-center gap-2 shadow-2xl shadow-cyan-500/10">
                        <tile.icon className="w-8 h-8 md:w-10 md:h-10 text-cyan-400" />
                        <span className="text-slate-300 text-xs font-medium uppercase tracking-wider">{tile.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Partners */}
      <section data-testid="partners-section" className="py-16 bg-slate-900 border-y border-slate-800 relative overflow-hidden">
        <div className="marquee-fade-left" />
        <div className="marquee-fade-right" />
        <Marquee speed={40} gradient={false}>
          {[...partners, ...partners].map((partner, index) => {
            const isLarge = partner.name === 'KillDisk' || partner.name === 'BitRaser' || partner.name === 'ThreatLocker';
            return (
              <div key={index} className="mx-8 bg-white rounded-lg px-10 py-6 flex items-center justify-center min-w-[200px] h-[100px]">
                <img src={partner.logo} alt={partner.name} width="160" height="64" loading="lazy" decoding="async" className={`object-contain ${isLarge ? 'max-h-20 max-w-[180px]' : 'max-h-16 max-w-[160px]'}`}
                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }} />
                <span className="font-bold text-slate-700 text-lg hidden">{partner.name}</span>
              </div>
            );
          })}
        </Marquee>
      </section>

      {/* Security Monitoring */}
      <section data-testid="monitoring-section" className="py-24 md:py-32 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <span className="text-cyan-400 text-sm font-mono uppercase tracking-widest mb-4 block">{t('home.monitorBadge')}</span>
            <h2 className="font-outfit text-3xl md:text-5xl font-semibold text-white mb-6">{t('home.monitorTitle')}</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">{t('home.monitorDesc')}</p>
          </motion.div>
          <SecurityMonitor />
        </div>
      </section>

      {/* Services Overview */}
      <section data-testid="services-section" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <span className="text-cyan-400 text-sm font-mono uppercase tracking-widest mb-4 block">{t('home.servicesBadge')}</span>
            <h2 className="font-outfit text-3xl md:text-5xl font-semibold text-white mb-6">{t('home.servicesTitle')}</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">{t('home.servicesDesc')}</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <GlowCard key={index} delay={index * 0.1} data-testid={`service-card-${index}`}>
                <div className="icon-container w-14 h-14 flex items-center justify-center mb-6">
                  <service.icon className="w-7 h-7 text-cyan-400" />
                </div>
                <h3 className="font-outfit text-xl font-semibold text-white mb-4">{service.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{service.description}</p>
              </GlowCard>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/cozumler">
              <Button data-testid="services-view-all" variant="outline" className="bg-transparent border border-slate-700 hover:border-cyan-500 text-slate-300 hover:text-cyan-400 rounded-none px-8 py-5 uppercase tracking-widest text-xs">
                {t('home.allServices')} <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Us */}
      <section data-testid="why-us-section" className="py-24 md:py-32 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <span className="text-cyan-400 text-sm font-mono uppercase tracking-widest mb-4 block">{t('home.whyUsBadge')}</span>
            <h2 className="font-outfit text-3xl md:text-5xl font-semibold text-white">{t('home.whyUsTitle')}</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {whyUs.map((item, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} data-testid={`why-us-card-${index}`} className="text-center">
                <div className="icon-container w-16 h-16 mx-auto flex items-center justify-center mb-6 rounded-full">
                  <item.icon className="w-8 h-8 text-cyan-400" />
                </div>
                <h3 className="font-outfit text-xl font-semibold text-white mb-3">{item.title}</h3>
                <p className="text-slate-400 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="cta-section" className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 cyber-grid opacity-50" />
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-outfit text-3xl md:text-5xl font-semibold text-white mb-6">{t('home.ctaTitle')}</h2>
            <p className="text-slate-400 mb-10 max-w-2xl mx-auto">{t('home.ctaDesc')}</p>
            <Link to="/iletisim">
              <Button 
                data-testid="cta-button" 
                data-agent-action="contact-request"
                aria-label="İletişime Geçin - Get in touch with ELC Teknoloji"
                className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-none px-10 py-6 uppercase tracking-widest text-sm btn-glow"
              >
                {t('home.ctaBtn')}
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
